import React, { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const MessageContainer = styled(Box)(({ theme, type = "error" }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    type === "error"
      ? "rgba(211, 47, 47, 0.04)"
      : type === "success"
        ? "rgba(46, 125, 50, 0.04)"
        : "rgba(2, 136, 209, 0.04)",
  border: `1px solid ${
    type === "error"
      ? theme.palette.error.main
      : type === "success"
        ? theme.palette.success.main
        : theme.palette.info.main
  }`,
  transition: "all 0.3s ease",
}));

const MessageIcon = styled(Box)(({ theme, type = "error" }) => ({
  marginRight: theme.spacing(2),
  color:
    type === "error"
      ? theme.palette.error.main
      : type === "success"
        ? theme.palette.success.main
        : theme.palette.info.main,
  display: "flex",
  alignItems: "center",
}));

const MessageText = styled(Typography)(({ theme, type = "error" }) => ({
  color:
    type === "error"
      ? theme.palette.error.main
      : type === "success"
        ? theme.palette.success.main
        : theme.palette.info.main,
  fontSize: "0.875rem",
  fontWeight: 500,
  flex: 1,
}));

function Message({ content, type = "error" }) {
  if (!content) return null;

  const getIcon = () => {
    switch (type) {
      case "error":
        return <ErrorOutlineIcon />;
      case "success":
        return <CheckCircleOutlineIcon />;
      default:
        return <InfoOutlinedIcon />;
    }
  };

  return (
    <MessageContainer type={type}>
      <MessageIcon type={type}>{getIcon()}</MessageIcon>
      <MessageText type={type}>{content}</MessageText>
    </MessageContainer>
  );
}

const UnifiedPaymentButton = ({ paymentType, items, onSuccess, clearCart }) => {
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const navigate = useNavigate();

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  useEffect(() => {
    console.log("PayPal Configuration:", {
      clientId: clientId ? "Present" : "Missing",
      apiUrl: process.env.REACT_APP_API_URL,
      paymentType,
      itemsCount: items?.length,
    });

    if (!clientId) {
      setMessage("PayPal configuration is missing. Please contact support.");
      setMessageType("error");
      return;
    }
    setPaypalLoaded(true);
  }, [clientId, paymentType, items]);

  const initialOptions = {
    "client-id": clientId,
    currency: "USD",
    intent: "capture",
  };

  const getEndpoint = () => {
    return "/payment/orders/create/";
  };

  const getCaptureEndpoint = (orderId) => {
    return `/payment/orders/${orderId}/capture/`;
  };

  const getSuccessRedirect = (responseData, orderId) => {
    switch (paymentType) {
      case "BOOK":
        return {
          path: "/payment-success",
          state: {
            paymentDetails: {
              orderData: responseData,
              items: items,
              orderId: orderId,
            },
          },
        };
      case "MAGAZINE":
        return {
          path: "/payment-success",
          state: {
            paymentDetails: {
              type: "MAGAZINE",
              magazine: items[0],
              purchaseId: responseData.purchase_id,
              status: responseData.status,
              message: responseData.message,
            },
          },
        };
      case "SUBSCRIPTION":
        return {
          path: "/payment-success",
          state: {
            paymentDetails: {
              ...responseData.subscription,
              status: responseData.status,
              message: responseData.message,
            },
          },
        };
      default:
        throw new Error("Invalid payment type");
    }
  };

  const createOrder = async () => {
    try {
      const endpoint = getEndpoint();
      const token = localStorage.getItem("token");

      console.log("Creating PayPal Order:", {
        endpoint,
        paymentType,
        itemsCount: items?.length,
        hasToken: !!token,
        apiUrl: process.env.REACT_APP_API_URL,
      });

      if (!token) {
        throw new Error(
          "Authentication token is missing. Please log in again.",
        );
      }

      const payload = {
        payment_type: paymentType,
        items: items.map((item) => ({
          id: item.id.toString(),
          name: item.title || item.name,
          price: parseFloat(item.price),
          quantity: item.quantity || 1,
        })),
      };

      console.log("Request Payload:", payload);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      console.log("API Response Status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error Response:", errorData);
        throw new Error(
          errorData.error ||
            errorData.detail ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const orderData = await response.json();
      console.log("Order Created Successfully:", orderData);
      return orderData.id;
    } catch (error) {
      console.error("PayPal create order error:", error);
      setMessageType("error");
      setMessage(`Could not initiate PayPal Checkout: ${error.message}`);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      const endpoint = getCaptureEndpoint(data.orderID);
      const payload =
        paymentType === "BOOK"
          ? { items, orderID: data.orderID }
          : { orderID: data.orderID };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        );
      }

      const responseData = await response.json();
      setMessageType("success");
      setMessage("Payment successful! Redirecting...");

      if (clearCart && paymentType === "BOOK") {
        clearCart();
      }

      if (onSuccess) {
        onSuccess(responseData);
      }

      const redirect = getSuccessRedirect(responseData, data.orderID);
      setTimeout(() => {
        navigate(redirect.path, {
          state: redirect.state,
          replace: true,
        });
      }, 2000);
    } catch (error) {
      setMessageType("error");
      setMessage(`Payment failed: ${error.message}`);
      throw error;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Message content={message} type={messageType} />
      {paypalLoaded && clientId ? (
        <PayPalScriptProvider options={initialOptions}>
          <PayPalButtons
            style={{
              shape: "rect",
              layout: "vertical",
            }}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={(err) => {
              console.error("PayPal Error:", err);
              setMessageType("error");
              setMessage(`PayPal encountered an error: ${err.message}`);
            }}
          />
        </PayPalScriptProvider>
      ) : (
        <Alert severity="warning" sx={{ mt: 2 }}>
          PayPal payment is currently unavailable. Please try HesabPay or
          contact support.
        </Alert>
      )}
    </Box>
  );
};

export default UnifiedPaymentButton;
